import { useState } from 'react';
import { Avatar, Box, Chip, Typography, makeStyles } from '@material-ui/core';
import ImageOutlined from '@material-ui/icons/ImageOutlined';
import Visibility from '@material-ui/icons/Visibility';
import FavoriteBorder from '@material-ui/icons/FavoriteBorder';
import ChatBubbleOutline from '@material-ui/icons/ChatBubbleOutline';
import ReplyOutlined from '@material-ui/icons/ReplyOutlined';
import EventOutlined from '@material-ui/icons/EventOutlined';
import VideocamOutlined from '@material-ui/icons/VideocamOutlined';

/**
 * One post as a card: thumbnail, creator, caption and its numbers.
 *
 * The drill-down used a dense table, which is fine for scanning but hopeless
 * for judging content — you cannot tell what a post IS from a caption snippet
 * in a cell. Matches the campaign post grid so the two read the same way.
 */

const useStyles = makeStyles((theme) => ({
  card: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    overflow: 'hidden',
    background: theme.palette.background.paper,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    transition: 'border-color 200ms ease, box-shadow 200ms ease',
    '&:hover': { borderColor: theme.palette.text.disabled, boxShadow: '0 2px 10px rgba(15,23,42,.08)' },
    '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  },
  media: { position: 'relative', width: '100%', paddingTop: '100%', background: '#e2e8f0' },
  image: { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' },
  noImage: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.disabled,
    fontSize: 12,
    textAlign: 'center',
    padding: theme.spacing(1),
  },
  topLeft: { position: 'absolute', top: 10, left: 10, display: 'flex', gap: 6, alignItems: 'center' },
  bottomRight: { position: 'absolute', bottom: 10, right: 10 },
  pill: {
    background: 'rgba(255,255,255,.92)',
    fontWeight: 600,
    fontSize: 11,
    height: 24,
  },
  iconPill: {
    background: 'rgba(255,255,255,.92)',
    borderRadius: '50%',
    width: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: { padding: theme.spacing(1.5, 1.75, 1.75), display: 'flex', flexDirection: 'column', flex: 1 },
  creatorRow: { display: 'flex', alignItems: 'center', gap: theme.spacing(1), marginBottom: theme.spacing(1) },
  avatar: { width: 28, height: 28, fontSize: 11 },
  creatorName: { fontWeight: 600, fontSize: 13, lineHeight: 1.2 },
  handle: { fontSize: 12, color: theme.palette.text.secondary },
  caption: {
    fontSize: 13.5,
    lineHeight: 1.45,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    marginBottom: theme.spacing(1.5),
  },
  stats: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  stat: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: theme.spacing(1.25),
    marginTop: 'auto',
  },
  date: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: theme.palette.text.secondary },
  er: { fontWeight: 700, fontSize: 12 },
}));

const initials = (name) =>
  String(name || '?')
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();

const prettyDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

function PostCard({ post, platformLabel, creatorName, formatNumber = (n) => n, formatPercentage, onClick }) {
  const classes = useStyles();
  // These URLs expire and are hotlink-protected, so a share of them will 404.
  // PostsTab tracks the same failure; without it a dead image is a blank box.
  const [imageFailed, setImageFailed] = useState(false);
  if (!post) return null;

  const name = post.creator_name || creatorName || post.creator_id;
  const date = prettyDate(post.posted_at);
  const isVideo = /reel|video|short/.test(String(post.post_type || ''));

  return (
    <Box
      className={classes.card}
      role="button"
      tabIndex={0}
      aria-label={`Post by ${name}. ${post.caption_snippet || ''}`}
      onClick={() => onClick && onClick(post)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick && onClick(post);
        }
      }}
    >
      <Box className={classes.media}>
        {post.thumbnail_url && !imageFailed ? (
          <img
            className={classes.image}
            src={post.thumbnail_url}
            alt=""
            loading="lazy"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <Box className={classes.noImage}>
            <ImageOutlined style={{ fontSize: 28, marginBottom: 4 }} />
            <span>{post.thumbnail_url ? 'Preview unavailable' : 'No preview'}</span>
          </Box>
        )}
        <Box className={classes.topLeft}>
          {isVideo && (
            <Box className={classes.iconPill}>
              <VideocamOutlined style={{ fontSize: 16, color: '#7c3aed' }} />
            </Box>
          )}
          {post.post_type && <Chip size="small" className={classes.pill} label={post.post_type} />}
        </Box>
        {platformLabel && (
          <Box className={classes.bottomRight}>
            <Chip size="small" className={classes.pill} label={platformLabel} />
          </Box>
        )}
      </Box>

      <Box className={classes.body}>
        <Box className={classes.creatorRow}>
          <Avatar className={classes.avatar} src={post.creator_image || undefined}>
            {initials(name)}
          </Avatar>
          <Box style={{ minWidth: 0 }}>
            <Typography className={classes.creatorName} noWrap>
              {name}
            </Typography>
            {post.creator_handle && (
              <Typography className={classes.handle} noWrap>
                @{String(post.creator_handle).replace(/^@/, '')}
              </Typography>
            )}
          </Box>
        </Box>

        <Typography className={classes.caption}>
          {post.caption_snippet || 'No caption'}
        </Typography>

        <Box className={classes.stats}>
          <Box className={classes.stat}>
            <Visibility style={{ fontSize: 15, color: '#64748b' }} />
            {formatNumber(post.view_count || 0)}
          </Box>
          <Box className={classes.stat}>
            <FavoriteBorder style={{ fontSize: 15, color: '#ec4899' }} />
            {formatNumber(post.like_count || 0)}
          </Box>
          <Box className={classes.stat}>
            <ChatBubbleOutline style={{ fontSize: 15, color: '#3b82f6' }} />
            {formatNumber(post.comment_count || 0)}
          </Box>
          <Box className={classes.stat}>
            <ReplyOutlined style={{ fontSize: 15, color: '#16a34a' }} />
            {formatNumber(post.share_count || 0)}
          </Box>
        </Box>

        <Box className={classes.footer}>
          <Box className={classes.date}>
            <EventOutlined style={{ fontSize: 14 }} />
            {date || 'Date unknown'}
          </Box>
          <Chip
            size="small"
            className={classes.er}
            label={`${formatPercentage ? formatPercentage(post.engagement_rate || 0) : `${post.engagement_rate || 0}%`} ER`}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default PostCard;
